import { isIncomeCategoryGroup } from './categoryGroups';
import { isExcludedFromFinancialTotals, isInternalTransfer } from './financialTransactionSemantics';

export const INCOME_TYPES = ['salary', 'variable', 'other'];

export const INCOME_TYPE_LABELS = {
  salary: 'Salary',
  variable: 'Variable Income',
  other: 'Other Income',
};

export const INCOME_PARENT_LABEL = 'Income';

export const INCOME_PICKER_OPTIONS = INCOME_TYPES.map((incomeType) => ({
  id: `income:${incomeType}`,
  name: INCOME_TYPE_LABELS[incomeType],
  parentName: INCOME_PARENT_LABEL,
  type: 'income',
  incomeType,
}));

const getCategoryKey = (category) => String(category?.category_key || category?.categoryKey || '').trim().toLowerCase();
const getParentId = (category) => category?.parent_id ?? category?.parentId ?? null;
const normalizeIncomeCategoryKey = (value) => String(value || '').trim().toLowerCase();

export const incomeTypeForCategory = (category) => {
  const key = getCategoryKey(category);
  if (key === 'income:salary') return 'salary';
  if (key === 'income:variable') return 'variable';
  if (key === 'income:other') return 'other';
  if (/^income:[a-z0-9][a-z0-9_-]*$/.test(key)) return key;
  return null;
};

export const isIncomeGroupCategory = (category) => (
  getCategoryKey(category) === 'income'
  || (getParentId(category) == null && String(category?.name || '').trim().toLowerCase() === 'income')
);

export const isIncomeCategory = (category) => Boolean(incomeTypeForCategory(category));

export const isSalaryIncomeCategory = (category) => getCategoryKey(category) === 'income:salary';

export const isIncomeSelectionValue = (value) => (
  typeof value === 'string' && value.startsWith('income:')
);

export const selectionValueToIncomeType = (value) => {
  if (!isIncomeSelectionValue(value)) return null;
  const incomeType = value.slice('income:'.length);
  if (INCOME_TYPES.includes(incomeType)) return incomeType;
  return /^income:[a-z0-9][a-z0-9_-]*$/.test(incomeType) ? incomeType : null;
};

export const isInflowTransactionAmount = (amount) => Number(amount) < 0;

export const canShowIncomeSection = (transactionOrAmount) => {
  if (typeof transactionOrAmount === 'object' && (
    isInternalTransfer(transactionOrAmount)
    || transactionOrAmount?.excludedFromIncome === true
    || transactionOrAmount?.excluded_from_income === true
  )) return false;
  const amount = typeof transactionOrAmount === 'object'
    ? transactionOrAmount?.amount
    : transactionOrAmount;
  return isInflowTransactionAmount(amount) || transactionOrAmount?.isIncome === true;
};

const normalizeSectionName = (value) => String(value || '').trim().toLowerCase();

export const getIncomeCategoryOptions = (categories = []) => {
  const incomeParent = categories.find(isIncomeGroupCategory);
  const children = incomeParent
    ? categories.filter((category) => Number(getParentId(category)) === Number(incomeParent.id))
    : categories.filter(isIncomeCategory);
  const options = children
    .map((category) => {
      const incomeType = incomeTypeForCategory(category);
      if (!incomeType) return null;
      return {
        id: `income:${incomeType}`,
        name: category.name || INCOME_TYPE_LABELS[incomeType] || INCOME_PARENT_LABEL,
        parentName: INCOME_PARENT_LABEL,
        type: 'income',
        incomeType,
        categoryId: category.id,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const leftCategory = children.find((category) => Number(category.id) === Number(left.categoryId));
      const rightCategory = children.find((category) => Number(category.id) === Number(right.categoryId));
      const leftPosition = Number.isFinite(leftCategory?.position) ? Number(leftCategory.position) : Number.MAX_SAFE_INTEGER;
      const rightPosition = Number.isFinite(rightCategory?.position) ? Number(rightCategory.position) : Number.MAX_SAFE_INTEGER;
      if (leftPosition !== rightPosition) return leftPosition - rightPosition;
      return left.name.localeCompare(right.name);
    });

  return options.length > 0 ? options : INCOME_PICKER_OPTIONS;
};

export const getIncomeTypeLabel = (incomeType, categories = []) => {
  const normalized = normalizeIncomeCategoryKey(incomeType);
  const matchedOption = getIncomeCategoryOptions(categories).find((option) => option.incomeType === normalized);
  return matchedOption?.name || INCOME_TYPE_LABELS[normalized] || INCOME_PARENT_LABEL;
};

export const buildPickerSections = ({ categories = [], includeIncome = false, inflowOnly = false }) => {
  const sections = [];

  if (includeIncome) {
    sections.push({
      id: 'income',
      name: INCOME_PARENT_LABEL,
      type: 'synthetic',
      options: getIncomeCategoryOptions(categories),
    });
  }

  const parents = categories.filter((category) => !getParentId(category) && !isIncomeGroupCategory(category));
  const childrenByParent = new Map();
  categories.forEach((category) => {
    const parentId = getParentId(category);
    if (!parentId || isIncomeCategory(category)) return;
    const existing = childrenByParent.get(parentId) || [];
    existing.push(category);
    childrenByParent.set(parentId, existing);
  });

  parents.forEach((parent) => {
    if (inflowOnly && normalizeSectionName(parent.name) !== 'excluded') {
      return;
    }

    const children = childrenByParent.get(parent.id) || [];
    if (children.length === 0) return;
    sections.push({
      id: `category:${parent.id}`,
      name: parent.name,
      type: 'category',
      options: children.map((child) => ({
        id: String(child.id),
        name: child.name,
        parentName: parent.name,
        type: 'category',
      })),
    });
  });

  return sections;
};

export const getTransactionDisplayCategory = (transaction, categories = []) => {
  if (isInternalTransfer(transaction)) {
    return {
      categoryName: 'Transfer',
      parentCategoryName: 'Excluded',
    };
  }
  if (transaction?.isIncome) {
    const split = Array.isArray(transaction?.incomeSplits) && transaction.incomeSplits.length > 0
      ? transaction.incomeSplits[0]
      : (Array.isArray(transaction?.splits) && transaction.splits.length > 0 ? transaction.splits[0] : null);
    const splitIncomeType = split?.incomeType;
    const incomeType = transaction?.incomeType || splitIncomeType || 'other';
    return {
      categoryName: transaction?.incomeLabel || split?.incomeLabel || getIncomeTypeLabel(incomeType, categories),
      parentCategoryName: INCOME_PARENT_LABEL,
    };
  }

  return {
    categoryName: transaction?.categoryName || 'Uncategorized',
    parentCategoryName: transaction?.parentCategoryName || null,
  };
};

export const isExpenseTransaction = (transaction) => {
  const amount = Number(transaction?.amount);
  const parentCategoryName = transaction?.parentCategoryName ?? transaction?.parent_category_name;
  return !isExcludedFromFinancialTotals(transaction)
    && transaction?.excludedFromSpending !== true
    && transaction?.excluded_from_spending !== true
    && transaction?.isIncome !== true
    && transaction?.is_income !== true
    && !isIncomeCategoryGroup(parentCategoryName)
    && Number.isFinite(amount)
    && amount !== 0;
};
